import { actionSet } from '@/service/driver/action_set'
import { SetType } from '@/service/driver/action_types'
import { ref, computed } from 'vue'
import i18n from '@/languages/i18n'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { DeviceType } from '@libs/types/enum'
import { getCurrentDevice } from '@/profiles/selector'
import { Fe87v2DeviceList } from '@/define/device_vendor'
import { DeviceManager } from '@/service/device'
import { VK, kks } from '@/define/define'
import { mouseBaseKeys, mediaKeys, kbVKTexture } from '@/define/vkcode'
import { operate } from '@/service/driver/operate'
import { texture } from '@/profiles/keyboard/keytexture/default'

export const actionMessage = ref<string>('')
export const isShowNormal = ref(true)
export const isShowMacro = ref(true)
export const isShowMagnet = ref(true)
export const isSupportKME = ref(false)

/**
 * 获取 VK 的显示文本 (如 0x04 -> "A")
 */
export const getVkText = (vk: VK): string => {
  return kbVKTexture[vk] || ''
}

/**
 * 核心 VK 数据清洗与兼容性管理 (用于 UI 显示层)
 */
export const cleanVksForDisplay = (vks: VK[]): VK[] => {
  const keys = [...(vks || [0, 0, 0])]
  // 1. 如果当前模式不支持组合键 (如 KME)，强制只显示主键
  if (!actionSet.isSupportCompose()) {
    keys[0] = 0
    keys[1] = 0
  }
  // 2. 如果当前模式不支持鼠标键，置空鼠标 VK
  if (!actionSet.isSupportMouse() && mouseBaseKeys().includes(keys[2])) {
    keys[2] = 0
  }
  // 3. 如果当前模式不支持多媒体键，置空多媒体 VK
  if (!actionSet.isSupportMultimedia() && mediaKeys().includes(keys[2])) {
    keys[2] = 0
  }
  return keys
}

/**
 * 获取当前选中的物理按键的显示名称 (如 "W", "Space")
 */
export const currentSelectedKeyText = computed(() => {
  const kk = kks.value[operate.currentKey.value]
  if (kk && texture[kk]) {
    return texture[kk].text
  }
  return ''
})

/**
 * 集中判断当前环境下是否支持特定模式 (Proxy 到 actionSet Service 层以保持逻辑一致)
 */
export const checkSupport = {
  /** 检查是否为特定的支持鼠标页签的设备群 (供 UI 装饰使用) */
  isMouseTabDevice: () => {
    return (
      specialCheckCurrent(speciaBehiver.DevicesOfZhihuiwuxian) ||
      specialCheckCurrent(speciaBehiver.DevicesOfHuoChaiRen) ||
      specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V1) ||
      specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V2) ||
      Fe87v2DeviceList.includes(DeviceManager.instance.state.currentPid)
    )
  },

  /** 是否支持组合键 */
  compose: () => actionSet.isSupportCompose(),

  /** 是否支持鼠标键 */
  mouse: () => actionSet.isSupportMouse(),

  /** 是否支持多媒体键 */
  multiMedia: () => actionSet.isSupportMultimedia(),

  /** 是否支持火力键 */
  fire: () => actionSet.isSupportFire(),
}

export const hasVisibleModes = computed(() => {
  return isShowNormal.value || isShowMacro.value || isShowMagnet.value || isSupportKME.value
})

export const setTexts = () => {
  const { t } = i18n.global
  return {
    [SetType.routine]: {
      title: t('set_type_routine_title'),
      type: 'Routine',
      desc: t('set_type_routine_desc'),
    },
    [SetType.dks]: {
      title: t('set_type_dks_title'),
      type: 'DKS',
      desc: t('set_type_dks_desc'),
    },
    [SetType.mt]: {
      title: t('set_type_mt_title'),
      type: 'MT',
      desc: t('set_type_mt_desc'),
    },
    [SetType.tgl]: {
      title: t('set_type_tgl_title'),
      type: 'TGL',
      desc: t('set_type_tgl_desc'),
    },
    [SetType.kme]: {
      title: t('set_type_kme_title_socd'),
      type: 'SOCD',
      desc: t('set_type_kme_desc'),
    },
    [SetType.macro]: {
      title: t('set_type_macro_title'),
      type: 'Macro',
      desc: t('set_type_macro_desc'),
    },
    [SetType.mpt]: {
      title: t('set_type_mpt_title'),
      type: 'MPT',
      desc: t('set_type_mpt_desc'),
      warn: t('set_type_mpt_warn'),
    },
    [SetType.end]: {
      title: t('set_type_end_title'),
      type: 'END',
      desc: t('set_type_end_desc'),
    },
    [SetType.rs]: {
      title: t('set_type_rs_title'),
      type: 'RS',
      desc: t('set_type_rs_desc'),
    },
  }
}
